// react_frontend/src/Components/Modules/HR/ProjectBoard.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Tooltip, Skeleton,
  Stack, Avatar, Divider, LinearProgress, Grid,
} from "@mui/material";
import {
  FaArrowLeft, FaPlus, FaEdit, FaTrash, FaClock, FaCheckCircle,
  FaSpinner, FaEye, FaFlag, FaUser, FaCalendarAlt,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

const BoardContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const KanbanColumn = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  background: "#f8f9fa",
  border: `2px solid ${color}20`,
  minHeight: 400,
}));

const TaskCard = styled(Paper)(({ theme, priority }) => {
  const colors = { high: "#ef4444", medium: "#f59e0b", low: "#10b981" };
  const c = colors[priority] || colors.medium;
  return {
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1.5),
    background: "white",
    borderLeft: `4px solid ${c}`,
    marginBottom: theme.spacing(1.5),
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  };
});

const columns = [
  { key: "todo", label: "To Do", color: "#6b7280", icon: <FaClock /> },
  { key: "in_progress", label: "In Progress", color: "#3b82f6", icon: <FaSpinner /> },
  { key: "review", label: "Review", color: "#f59e0b", icon: <FaEye /> },
  { key: "done", label: "Done", color: "#10b981", icon: <FaCheckCircle /> },
];

const ProjectBoard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", status: "todo", priority: "medium",
    assigned_to: "", due_date: ""
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projRes, taskRes, userRes] = await Promise.all([
        AxiosAuthInstance.get(`hr/projects/${id}`),
        AxiosAuthInstance.get(`hr/projects/${id}/tasks`),
        AxiosAuthInstance.get("hr/projects/users/assignable"),
      ]);
      setProject(projRes.data.data.project);
      setTasks(taskRes.data.data || []);
      setUsers(userRes.data.data || []);
    } catch (err) {
      Swal.fire("Error", "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setForm({
        title: task.title,
        description: task.description || "",
        status: task.status,
        priority: task.priority,
        assigned_to: task.assigned_to || "",
        due_date: task.due_date || "",
      });
    } else {
      setEditingTask(null);
      setForm({ title: "", description: "", status: "todo", priority: "medium", assigned_to: "", due_date: "" });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      Swal.fire("Warning", "Task title is required", "warning");
      return;
    }
    try {
      if (editingTask) {
        await AxiosAuthInstance.put(`hr/projects/tasks/${editingTask.id}`, form);
        Swal.fire({ icon: "success", title: "Updated!", timer: 1500, showConfirmButton: false });
      } else {
        await AxiosAuthInstance.post("hr/projects/tasks", { ...form, project_id: parseInt(id) });
        Swal.fire({ icon: "success", title: "Created!", timer: 1500, showConfirmButton: false });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Operation failed", "error");
    }
  };

  const handleDelete = async (task) => {
    const result = await Swal.fire({
      title: "Delete Task?",
      text: `"${task.title}" will be permanently deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      confirmButtonText: "Yes, Delete",
    });
    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`hr/projects/tasks/${task.id}`);
        Swal.fire("Deleted!", "Task deleted", "success");
        fetchData();
      } catch (err) {
        Swal.fire("Error", "Failed to delete", "error");
      }
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await AxiosAuthInstance.put(`hr/projects/tasks/${task.id}`, { status: newStatus });
      fetchData();
    } catch (err) {
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const getTasksByStatus = (status) => tasks.filter((t) => t.status === status);

  const getPriorityIcon = (priority) => <FaFlag size={10} />;
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "#ef4444";
      case "medium": return "#f59e0b";
      case "low": return "#10b981";
      default: return "#6b7280";
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumb title="Project Board" />
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2, mb: 3 }} />
        <BoardContainer>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          ))}
        </BoardContainer>
      </Box>
    );
  }

    return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title={project?.name || "Project Board"} />

      {/* ✅ Header with Prominent Add Task Button */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: "2px solid #667eea" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/hr/projects")} sx={{ color: "#667eea" }}>
              <FaArrowLeft />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={700}>{project?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {project?.description || "No description"}
              </Typography>
            </Box>
          </Box>
          
          {/* ✅ বড় এবং স্পষ্ট Add Task বাটন */}
          <Button
            variant="contained"
            size="large"
            startIcon={<FaPlus />}
            onClick={() => openDialog()}
            sx={{ 
              bgcolor: "#667eea", 
              "&:hover": { bgcolor: "#5568d3" }, 
              textTransform: "none",
              px: 3,
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 700,
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
            }}
          >
            + Add New Task
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Overall Progress</Typography>
            <Typography variant="caption" fontWeight={700}>
              {tasks.filter((t) => t.status === "done").length} / {tasks.length} tasks done
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={tasks.length ? (tasks.filter((t) => t.status === "done").length / tasks.length) * 100 : 0}
            sx={{ height: 8, borderRadius: 4, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "#667eea" } }}
          />
        </Box>
      </Paper>

      <BoardContainer>
        {columns.map((col) => {
          const colTasks = getTasksByStatus(col.key);
          return (
            <KanbanColumn key={col.key} color={col.color} elevation={0}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, pb: 1, borderBottom: `2px solid ${col.color}` }}>
                <Box sx={{ color: col.color }}>{col.icon}</Box>
                <Typography variant="subtitle1" fontWeight={700}>{col.label}</Typography>
                <Chip label={colTasks.length} size="small" sx={{ ml: "auto", bgcolor: col.color, color: "white", fontWeight: 700 }} />
              </Box>

              {colTasks.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No tasks in {col.label}
                  </Typography>
                  {/* ✅ কলামেও Add Task বাটন */}
                  <Button
                    size="small"
                    startIcon={<FaPlus />}
                    onClick={() => openDialog()}
                    sx={{ color: col.color, textTransform: "none" }}
                  >
                    Add Task
                  </Button>
                </Box>
              ) : (
                colTasks.map((task) => (
                  <TaskCard key={task.id} priority={task.priority} elevation={0}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1, pr: 1 }}>
                        {task.title}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openDialog(task)} sx={{ p: 0.5 }}>
                            <FaEdit size={11} color="#ff9800" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(task)} sx={{ p: 0.5 }}>
                            <FaTrash size={11} color="#f44336" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {task.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, maxHeight: 32, overflow: "hidden" }}>
                        {task.description}
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                      <Chip
                        size="small"
                        icon={<FaFlag size={10} />}
                        label={task.priority}
                        sx={{
                          bgcolor: `${getPriorityColor(task.priority)}15`,
                          color: getPriorityColor(task.priority),
                          fontWeight: 600, fontSize: "0.7rem",
                          "& .MuiChip-icon": { color: getPriorityColor(task.priority) },
                        }}
                      />
                      {task.due_date && (
                        <Chip
                          size="small"
                          icon={<FaCalendarAlt />}
                          label={task.due_date}
                          sx={{
                            bgcolor: task.is_overdue ? "#ffebee" : "#f5f5f5",
                            color: task.is_overdue ? "#c62828" : "#757575",
                            fontSize: "0.7rem",
                          }}
                        />
                      )}
                    </Box>

                    {task.assignee_name && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                        <Avatar sx={{ width: 20, height: 20, fontSize: "0.6rem", bgcolor: "#667eea" }}>
                          {task.assignee_name.charAt(0)}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">{task.assignee_name}</Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", gap: 0.5, mt: 1, pt: 1, borderTop: "1px solid #f0f0f0" }}>
                      {columns.filter((c) => c.key !== col.key).map((c) => (
                        <Tooltip key={c.key} title={`Move to ${c.label}`}>
                          <IconButton
                            size="small"
                            onClick={() => handleStatusChange(task, c.key)}
                            sx={{ p: 0.3, color: c.color }}
                          >
                            {c.icon}
                          </IconButton>
                        </Tooltip>
                      ))}
                    </Box>
                  </TaskCard>
                ))
              )}
            </KanbanColumn>
          );
        })}
      </BoardContainer>

      {/* Task Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#667eea", color: "white" }}>
          {editingTask ? "Edit Task" : "Create New Task"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth label="Task Title *" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              fullWidth label="Description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline rows={3}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Status *</InputLabel>
                  <Select value={form.status} label="Status *"
                    onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {columns.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority *</InputLabel>
                  <Select value={form.priority} label="Priority *"
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Assign To</InputLabel>
              <Select value={form.assigned_to} label="Assign To"
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
                <MenuItem value="">Unassigned</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth label="Due Date" type="date" value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}
            sx={{ bgcolor: "#667eea", "&:hover": { bgcolor: "#5568d3" } }}>
            {editingTask ? "Update" : "Create Task"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectBoard;